/**
 * FilterBar — toolbar above the invoice table.
 *
 * Layout mirrors `prototype/sales/invoices.html:1522-1552`:
 *
 *   [ 🔍 Search… ] [ 🔎 View · All open ⌄ ] [ 👤 Customer ⌄ ]
 *   [ 📅 Date range ⌄ ] [ ₹ Amount ⌄ ] → spacer → [ ⬇ Export ]
 *
 * Every control is **36 px tall (`h-9`), `rounded-sm`** to match the
 * prototype's `.search-input` / `.saved-view` / `.filter-pill`
 * rules. The active-state tint uses **sky** in place of the
 * prototype's indigo — Flint's token ladder has only sky for the
 * accent (`tokens.css`), so indigo-50/100/700 → sky-50/100/700.
 *
 * V1.0.x ships every functional bit except saved-view (the "View ·
 * All open" pill remains a disabled visual stub — persistence lands
 * in V1.x once `saved_views` storage is added). Amount filter and
 * CSV export are live: amount runs client-side against the already
 * rendered rows (server slice is still a V1.x add), export writes
 * a CSV of the current visible rows.
 */
import { useEffect, useRef, useState } from "react";
import {
  Calendar,
  Check,
  ChevronDown,
  Download,
  IndianRupee,
  ListFilter,
  Plus,
  Search as SearchIcon,
  Trash2,
  User as UserIcon,
  X,
} from "lucide-react";
import {
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Input,
  Label,
} from "@flint/ui";
import { CustomerPicker, type CustomerSummary } from "../customer-picker.tsx";
import type { SavedInvoiceView } from "./saved-views.ts";

export interface FilterBarProps {
  search: string;
  onSearchChange: (next: string) => void;
  customer: CustomerSummary | null;
  onCustomerChange: (next: CustomerSummary | null) => void;
  dateFrom: string | null;
  dateTo: string | null;
  onDateChange: (range: { from: string | null; to: string | null }) => void;
  /** Rupees — display units; converted to paise before comparing rows. */
  amountMin: string | null;
  amountMax: string | null;
  onAmountChange: (range: { min: string | null; max: string | null }) => void;
  /** Fires on Export click; parent owns the download call (it has the rows). */
  onExport: () => void;
  /** Disable the export button (e.g. no rows to write). */
  exportDisabled?: boolean;
  /** Saved views loaded from localStorage; passed through so the parent
   * can react to changes without re-reading storage on every render. */
  savedViews: SavedInvoiceView[];
  /** Fires when user picks a saved view — parent applies the snapshot. */
  onApplySavedView: (view: SavedInvoiceView) => void;
  /** Fires when user clicks "Save current view as..." — parent prompts + persists. */
  onSaveCurrentView: () => void;
  /** Fires when user deletes a saved view. */
  onDeleteSavedView: (viewId: string) => void;
  /** ID of the currently applied saved view (drives the pill label + active check). */
  activeSavedViewId: string | null;
}

export function FilterBar({
  search,
  onSearchChange,
  customer,
  onCustomerChange,
  dateFrom,
  dateTo,
  onDateChange,
  amountMin,
  amountMax,
  onAmountChange,
  onExport,
  exportDisabled = false,
  savedViews,
  onApplySavedView,
  onSaveCurrentView,
  onDeleteSavedView,
  activeSavedViewId,
}: FilterBarProps) {
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [amountOpen, setAmountOpen] = useState(false);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Search — h-9 rectangular box, matching `.search-input` from
          the prototype. Inlined rather than reusing the `<Input>`
          primitive (which is `h-10`) so the row stays visually flush
          with the 36 px pills next to it. */}
      <label className="relative flex-1 min-w-[240px] max-w-[420px]">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none">
          <SearchIcon size={14} aria-hidden />
        </span>
        <input
          type="search"
          aria-label="Search"
          placeholder="Search invoice number, customer…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className={[
            "h-9 w-full pl-9 pr-8 rounded-sm border border-line bg-bg text-sm text-ink",
            "placeholder:text-muted",
            "hover:border-line-2",
            "focus:outline-none focus:border-sky-600 focus:bg-paper",
            "focus:shadow-[0_0_0_3px_var(--color-sky-100)]",
            "transition-colors",
          ].join(" ")}
        />
        {search ? (
          <button
            type="button"
            onClick={() => onSearchChange("")}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 grid h-5 w-5 place-items-center rounded-sm text-muted hover:text-ink-2 hover:bg-raised"
          >
            <X size={12} />
          </button>
        ) : null}
      </label>

      <SavedViewMenu
        savedViews={savedViews}
        activeSavedViewId={activeSavedViewId}
        onApply={onApplySavedView}
        onSaveCurrent={onSaveCurrentView}
        onDelete={onDeleteSavedView}
      />

      {/* Customer pill */}
      <FilterPill
        active={customer !== null}
        icon={<UserIcon size={12} />}
        label={customer ? customer.name : "Customer"}
        onClick={() => setCustomerPickerOpen(true)}
        onClear={customer ? () => onCustomerChange(null) : undefined}
      />

      {/* Date range pill */}
      <FilterPill
        active={dateFrom !== null || dateTo !== null}
        icon={<Calendar size={12} />}
        label={
          dateFrom && dateTo
            ? `${dateFrom} → ${dateTo}`
            : dateFrom
              ? `After ${dateFrom}`
              : dateTo
                ? `Before ${dateTo}`
                : "Date range"
        }
        onClick={() => setDateOpen(true)}
        onClear={dateFrom || dateTo ? () => onDateChange({ from: null, to: null }) : undefined}
      />

      {/* Amount range pill */}
      <FilterPill
        active={amountMin !== null || amountMax !== null}
        icon={<IndianRupee size={12} />}
        label={
          amountMin && amountMax
            ? `₹${amountMin} → ₹${amountMax}`
            : amountMin
              ? `≥ ₹${amountMin}`
              : amountMax
                ? `≤ ₹${amountMax}`
                : "Amount"
        }
        onClick={() => setAmountOpen(true)}
        onClear={
          amountMin || amountMax ? () => onAmountChange({ min: null, max: null }) : undefined
        }
      />

      {/* Spacer pushes the Export to the right edge on wide layouts. */}
      <span className="flex-1 min-w-0" />

      {/* Export — writes the currently visible rows as CSV. Disabled
          when there are no rows to export (empty tab/filter state). */}
      <Button
        type="button"
        intent="ghost"
        size="md"
        disabled={exportDisabled}
        onClick={onExport}
        title={exportDisabled ? "No rows to export." : "Download current list as Excel (.xlsx)"}
        className="border border-line"
      >
        <Download size={13} />
        <span>Export</span>
      </Button>

      {/* Customer picker dialog (reuses the composer's picker) */}
      <DialogRoot open={customerPickerOpen} onOpenChange={setCustomerPickerOpen}>
        <DialogContent className="sm:max-w-[560px] p-0 gap-0">
          <DialogHeader className="p-4 border-b border-line">
            <DialogTitle>Filter by customer</DialogTitle>
            <DialogDescription>
              Pick a customer to limit the list to their invoices.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4">
            <CustomerPicker
              selected={customer}
              onSelect={(c) => {
                onCustomerChange(c);
                setCustomerPickerOpen(false);
              }}
            />
          </div>
        </DialogContent>
      </DialogRoot>

      {/* Date range dialog */}
      <DateRangeDialog
        open={dateOpen}
        onOpenChange={setDateOpen}
        from={dateFrom ?? ""}
        to={dateTo ?? ""}
        onApply={(range) => {
          onDateChange({
            from: range.from || null,
            to: range.to || null,
          });
          setDateOpen(false);
        }}
      />

      {/* Amount range dialog */}
      <AmountRangeDialog
        open={amountOpen}
        onOpenChange={setAmountOpen}
        min={amountMin ?? ""}
        max={amountMax ?? ""}
        onApply={(range) => {
          onAmountChange({
            min: range.min || null,
            max: range.max || null,
          });
          setAmountOpen(false);
        }}
      />
    </div>
  );
}

// ── saved-view menu ────────────────────────────────────────────────

/**
 * Sky-tinted pill matching the prototype's `.saved-view`. Clicking
 * opens a menu with all persisted views + a "Save current view as..."
 * action. Views persist in localStorage (per company + user).
 */
function SavedViewMenu({
  savedViews,
  activeSavedViewId,
  onApply,
  onSaveCurrent,
  onDelete,
}: {
  savedViews: SavedInvoiceView[];
  activeSavedViewId: string | null;
  onApply: (view: SavedInvoiceView) => void;
  onSaveCurrent: () => void;
  onDelete: (viewId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const activeName =
    savedViews.find((v) => v.id === activeSavedViewId)?.name ?? "All open";

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Saved views"
        className={[
          "inline-flex items-center gap-1.5 h-9 px-3 rounded-sm",
          "border border-sky-100 bg-sky-50 text-sky-700",
          "text-xs font-semibold whitespace-nowrap",
          "hover:border-sky-200 hover:bg-sky-100",
        ].join(" ")}
      >
        <ListFilter size={13} aria-hidden />
        <span>View · {activeName}</span>
        <ChevronDown size={11} aria-hidden className="opacity-70" />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute left-0 top-full z-20 mt-1 min-w-[240px] rounded-sm border border-line bg-paper shadow-lg"
        >
          <div className="max-h-64 overflow-auto py-1">
            {savedViews.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted">
                No saved views yet. Set some filters, then choose "Save current view as…".
              </div>
            ) : (
              savedViews.map((v) => (
                <div
                  key={v.id}
                  className={[
                    "group flex items-center justify-between gap-1 pl-3 pr-1 py-1.5 hover:bg-bg",
                    v.id === activeSavedViewId ? "bg-sky-50" : "",
                  ].join(" ")}
                >
                  <button
                    type="button"
                    onClick={() => {
                      onApply(v);
                      setOpen(false);
                    }}
                    className="flex flex-1 items-center gap-2 text-left text-xs text-ink"
                  >
                    {v.id === activeSavedViewId ? (
                      <Check size={12} className="text-sky-700" aria-hidden />
                    ) : (
                      <span className="w-3" aria-hidden />
                    )}
                    <span className="truncate">{v.name}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(v.id)}
                    aria-label={`Delete "${v.name}"`}
                    className="opacity-0 group-hover:opacity-100 text-muted hover:text-bad p-1"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="border-t border-line">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onSaveCurrent();
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-sky-700 hover:bg-sky-50"
            >
              <Plus size={12} aria-hidden />
              Save current view as…
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── filter pill ────────────────────────────────────────────────────

/**
 * Rectangular filter pill, 36 px tall — mirrors `.filter-pill` in
 * `prototype/sales/invoices.html:865-882`. Active state uses the Flint sky
 * palette in place of the prototype's indigo.
 */
function FilterPill({
  active,
  icon,
  label,
  onClick,
  onClear,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  onClear?: (() => void) | undefined;
}) {
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border whitespace-nowrap text-xs font-medium transition-colors",
        active
          ? "border-sky-100 bg-sky-50 text-sky-700"
          : "border-line bg-paper text-ink-2 hover:bg-bg hover:border-line-2",
      ].join(" ")}
    >
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 cursor-pointer"
      >
        <span className={active ? "text-sky-700" : "text-muted"}>{icon}</span>
        <span className="truncate max-w-[180px]">{label}</span>
        <ChevronDown
          size={11}
          aria-hidden
          className={active ? "text-sky-700 opacity-80" : "text-muted opacity-70"}
        />
      </button>
      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          aria-label={`Clear ${label}`}
          className="text-muted hover:text-ink-2 -mr-1"
        >
          <X size={12} />
        </button>
      ) : null}
    </span>
  );
}

// ── date-range dialog ──────────────────────────────────────────────

function DateRangeDialog({
  open,
  onOpenChange,
  from,
  to,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  from: string;
  to: string;
  onApply: (range: { from: string; to: string }) => void;
}) {
  const [localFrom, setLocalFrom] = useState(from);
  const [localTo, setLocalTo] = useState(to);

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Filter by issue date</DialogTitle>
          <DialogDescription>
            Limit the list to invoices issued within a date range.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="date-from">From</Label>
            <Input
              id="date-from"
              type="date"
              value={localFrom}
              onChange={(e) => setLocalFrom(e.target.value)}
              className="font-mono"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="date-to">To</Label>
            <Input
              id="date-to"
              type="date"
              value={localTo}
              onChange={(e) => setLocalTo(e.target.value)}
              className="font-mono"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button
            type="button"
            intent="ghost"
            onClick={() => {
              setLocalFrom("");
              setLocalTo("");
              onApply({ from: "", to: "" });
            }}
          >
            Clear
          </Button>
          <Button type="button" onClick={() => onApply({ from: localFrom, to: localTo })}>
            Apply
          </Button>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}

// ── amount-range dialog ────────────────────────────────────────────

/**
 * Amount range in rupees. Values render as `≥ ₹X`, `≤ ₹Y`, or
 * `₹X → ₹Y` when both are set. Non-numeric or negative values are
 * refused; empty means "no lower/upper bound".
 */
function AmountRangeDialog({
  open,
  onOpenChange,
  min,
  max,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  min: string;
  max: string;
  onApply: (range: { min: string; max: string }) => void;
}) {
  const [localMin, setLocalMin] = useState(min);
  const [localMax, setLocalMax] = useState(max);

  const minValid = localMin === "" || (Number.isFinite(Number(localMin)) && Number(localMin) >= 0);
  const maxValid = localMax === "" || (Number.isFinite(Number(localMax)) && Number(localMax) >= 0);
  const rangeValid =
    localMin === "" || localMax === "" || Number(localMin) <= Number(localMax);
  const applyDisabled = !minValid || !maxValid || !rangeValid;

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Filter by amount</DialogTitle>
          <DialogDescription>
            Limit the list to invoices whose total is within a rupee range.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="amount-min">Min ₹</Label>
            <Input
              id="amount-min"
              inputMode="decimal"
              placeholder="0"
              value={localMin}
              onChange={(e) => setLocalMin(e.target.value.trim())}
              aria-invalid={!minValid}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="amount-max">Max ₹</Label>
            <Input
              id="amount-max"
              inputMode="decimal"
              placeholder="No upper limit"
              value={localMax}
              onChange={(e) => setLocalMax(e.target.value.trim())}
              aria-invalid={!maxValid}
            />
          </div>
        </div>
        {!rangeValid ? (
          <p className="text-xs text-rose-700 mt-1" role="alert">
            Min must be ≤ Max.
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button
            type="button"
            intent="ghost"
            onClick={() => {
              setLocalMin("");
              setLocalMax("");
              onApply({ min: "", max: "" });
            }}
          >
            Clear
          </Button>
          <Button
            type="button"
            disabled={applyDisabled}
            onClick={() => onApply({ min: localMin, max: localMax })}
          >
            Apply
          </Button>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}
